export const STAFF_WORKERS_PAGE_SIZE = 25;
export const STAFF_WORKER_SESSIONS_PAGE_SIZE = 100;

export const STAFF_WORKER_STATUS = {
    checkedIn: "checked_in",
    notCheckedIn: "not_checked_in",
    onSiteNow: "on_site_now",
};


const normalizeID = (value) => {
    const normalizedValue = String(value ?? "").trim();

    return normalizedValue || undefined;
};

export const normalizeStaffWorkerRoleIDs = (roles = []) => (
    [ ...new Set(
        (Array.isArray(roles) ? roles : [])
            .map((role) => normalizeID(role?.id ?? role))
            .filter(Boolean)
    ) ].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
);

export const getStaffWorkerRoleColor = (colorHex) => {
    const normalizedColor = String(colorHex || "").trim();

    return /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(normalizedColor) ?
        normalizedColor :
        "#FFFFFF";
};

export const buildStaffWorkersWhere = ({
    eligibleRoleIDs,
    selectedRoleID,
    subcontractorID,
} = {}) => {
    const normalizedEligibleRoleIDs = normalizeStaffWorkerRoleIDs(eligibleRoleIDs);
    const normalizedSelectedRoleID = normalizeID(selectedRoleID);
    const hasSelectedEligibleRole = (
        normalizedSelectedRoleID &&
        normalizedEligibleRoleIDs.includes(normalizedSelectedRoleID)
    );
    let where = hasSelectedEligibleRole ? {
        column: "ROLE_ID",
        operator: "IN",
        value: [ normalizedSelectedRoleID ],
    } : undefined;
    const normalizedSubcontractorID = normalizeID(subcontractorID);

    if (normalizedSubcontractorID)
    {
        const subcontractorWhere = {
            column: "SUBCONTRACTOR_ID",
            operator: "EQ",
            value: normalizedSubcontractorID,
        };

        where = where ? { ...where, AND: [ subcontractorWhere ] } : subcontractorWhere;
    }

    return where;
};

export const getStaffWorkersQueryState = ({
    eventRoles,
    selectedRoleID,
    subcontractorID,
} = {}) => {
    const eligibleRoleIDs = normalizeStaffWorkerRoleIDs(eventRoles);
    const where = buildStaffWorkersWhere({
        eligibleRoleIDs,
        selectedRoleID,
        subcontractorID,
    });

    return {
        eligibleRoleIDs,
        where,
    };
};

export const normalizeStaffWorkerIDs = (workers = []) => (
    [ ...new Set(
        (Array.isArray(workers) ? workers : [])
            .map((worker) => normalizeID(worker?.id ?? worker))
            .filter(Boolean)
    ) ].sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
);

export const buildStaffWorkerSessionsWhere = ({ eeID, workerIDs = [] } = {}) => {
    const normalizedEeID = normalizeID(eeID);
    const normalizedWorkerIDs = normalizeStaffWorkerIDs(workerIDs);

    if (!normalizedEeID || !normalizedWorkerIDs.length)
    {
        return undefined;
    }

    return {
        column: "EE_ID",
        operator: "EQ",
        value: normalizedEeID,
        AND: [ {
            column: "USER_ID",
            operator: "IN",
            value: normalizedWorkerIDs,
        } ],
    };
};

const getSessionWorkerID = (session) => normalizeID(
    session?.user_id ?? session?.relUser?.id
);

const getSessionWorkedSeconds = (session) => {
    if (session?.getWorkedHours === null || session?.getWorkedHours === undefined || session?.getWorkedHours === "")
    {
        return undefined;
    }

    const hours = Number(session.getWorkedHours);

    return Number.isFinite(hours) && hours >= 0 ? Math.round(hours * 3600) : undefined;
};

const isActiveWorkerSession = (session) => {
    const status = String(session?.getCheckOutStatus || "").trim().toUpperCase();

    if (status)
    {
        return status === "IN_PROGRESS" || status === "FAILED_CHECKOUT";
    }

    return !session?.check_out_at;
};

export const getStaffWorkerSessionSummaries = (workers = [], sessions = []) => {
    const summaries = normalizeStaffWorkerIDs(workers).reduce((result, workerID) => ({
        ...result,
        [ workerID ]: {
            status: STAFF_WORKER_STATUS.notCheckedIn,
            totalWorkedSeconds: null,
        },
    }), {});
    const aggregation = {};

    (Array.isArray(sessions) ? sessions : []).filter(Boolean).forEach((session) => {
        const workerID = getSessionWorkerID(session);

        if (!workerID || !summaries[ workerID ])
        {
            return;
        }

        const workedSeconds = getSessionWorkedSeconds(session);
        const workerAggregation = aggregation[ workerID ] || {
            hasIncompleteHours: false,
            hasActiveSession: false,
            totalWorkedSeconds: 0,
        };

        workerAggregation.hasActiveSession = workerAggregation.hasActiveSession || (
            isActiveWorkerSession(session)
        );

        if (workedSeconds === undefined)
        {
            workerAggregation.hasIncompleteHours = true;
        }
        else
        {
            workerAggregation.totalWorkedSeconds += workedSeconds;
        }

        aggregation[ workerID ] = workerAggregation;
    });

    Object.entries(aggregation).forEach(([ workerID, workerAggregation ]) => {
        const onSiteNow = workerAggregation.hasActiveSession;

        summaries[ workerID ] = {
            status: onSiteNow ? STAFF_WORKER_STATUS.onSiteNow : STAFF_WORKER_STATUS.checkedIn,
            totalWorkedSeconds: onSiteNow || workerAggregation.hasIncompleteHours ?
                null :
                workerAggregation.totalWorkedSeconds,
        };
    });

    return summaries;
};

export const formatStaffWorkerDuration = (seconds) => {
    if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds)) || Number(seconds) < 0)
    {
        return undefined;
    }

    const normalizedSeconds = Number(seconds);
    const hours = Math.floor(normalizedSeconds / 3600);
    const minutes = Math.floor((normalizedSeconds % 3600) / 60);

    return `${ hours } h ${ minutes } min`;
};
